import { AlertCircle, CheckCircle2, CircleHelp } from "lucide-react";

import {
  CodeSyntaxValidation,
  IgMonoText,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";

/**
 * Compact TypeScript status for the editor header. Valid → a calm "Syntax OK".
 * Invalid → an error count plus the diagnostic *headline* (first line only),
 * truncated to a single line so it never floods the header; hovering reveals
 * the location and the full elaboration chain in a monospace tooltip. The
 * editor's own inline squiggles and hovers remain the precise per-error
 * navigation. The pending "Checking TypeScript…" state has no count and nothing
 * to expand, so it renders as a plain status with no tooltip.
 */
export const SyntaxStatus = ({ syntax }: { syntax: CodeSyntaxValidation }) => {
  const theme = useTheme();

  if (syntax.isValid) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ flexShrink: 0 }}
      >
        <CheckCircle2 size={1} color={theme.palette.success.main} />
        <IgTypography
          variant="body2"
          fontSize={10}
          fontWeight={500}
          color="success.main"
        >
          Syntax OK
        </IgTypography>
      </Stack>
    );
  }

  const headline = syntax.errorMessage?.split("\n")[0] ?? "TypeScript error";
  const isCheckingTypeScript = headline === "Checking TypeScript...";
  const hasCount = syntax.errorCount > 0;
  const countLabel = `${syntax.errorCount} ${
    syntax.errorCount === 1 ? "error" : "errors"
  }`;
  const locationLabel =
    syntax.errorLine !== null
      ? `Line ${syntax.errorLine}${
          syntax.errorColumn !== null ? `, column ${syntax.errorColumn}` : ""
        }`
      : null;

  const statusColor = isCheckingTypeScript
    ? theme.palette.info.main
    : theme.palette.error.main;
  const statusTypographyColor = isCheckingTypeScript
    ? "info.main"
    : "error.main";
  const StatusIcon = isCheckingTypeScript ? CircleHelp : AlertCircle;

  const status = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ flexShrink: 0 }}
      >
        <StatusIcon size={16} color={statusColor} />
        <IgTypography
          variant="body2"
          fontWeight={600}
          color={statusTypographyColor}
        >
          {hasCount ? countLabel : headline}
        </IgTypography>
      </Stack>
      {hasCount ? (
        <IgTypography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ minWidth: 0 }}
        >
          {headline}
        </IgTypography>
      ) : null}
    </Stack>
  );

  if (!hasCount) return status;

  return (
    <IgTooltip
      placement="bottom-end"
      maxWidth={520}
      title={
        <Stack spacing={0.5} sx={{ maxHeight: 320, overflow: "auto" }}>
          {locationLabel ? (
            <IgTypography variant="body2" fontWeight={700}>
              {locationLabel}
            </IgTypography>
          ) : null}
          <IgMonoText
            sx={{ fontSize: "12px" }}
            value={syntax.errorMessage || "Unavailable"}
          />
        </Stack>
      }
    >
      {status}
    </IgTooltip>
  );
};
